import { useState, useContext, useCallback, useMemo, useEffect } from "react"

import { useTranslation } from "next-i18next"
import { LanguageContext } from "../../contexts/languageContext"
import FormInput from "../../components/FormInput"

import { useRouter } from "next/router"
import ProgressSteps from "../../components/ProgressSteps/"
import RandomFact from "../../components/RandomFact/"
import Image from "next/image"
import Button from "../../components/Button"
import { languagesPronouns, solaceGenders } from "../../lib/constants"

import { useCheckAuth } from "../../hooks/useRequireAuth"
import useUser from "../../hooks/useUser"

import { buildAbsoluteUrl, goToSignUp } from "../../utils/browser"
import CountryAndLanguageSelector from "../../components/CountryAndLanguageSelector/"
import SessionStepsLoading from "./SessionStepsLoading"

import {
  areaOptions,
  approachOptions,
  typeOptions,
  stepNames,
  durationOptions,
} from "./SessionSteps.constants"

function SessionSteps({ mode = "solace" }) {
  const { t } = useTranslation("common")

  const { query, push } = useRouter()
  const [loading, setLoading] = useState(true)
  const [loadingStartSession, setLoadingStartSession] = useState(false)
  const [name, setName] = useState("")
  const [pronoun, setPronoun] = useState("")
  const [area, setArea] = useState("")
  const [duration, setDuration] = useState("")
  const [areaCustom, setAreaCustom] = useState("")
  const [pronounCustom, setPronounCustom] = useState("")
  const [approach, setApproach] = useState("")
  const [step, setStep] = useState(0)
  const [type, setType] = useState(query.type || "classic")

  const { authenticated } = useCheckAuth()

  const redirectUrl = localStorage.getItem("redirectUrl")

  const hasRedirectUrl = redirectUrl && redirectUrl.includes("/")

  useEffect(() => {
    if (!hasRedirectUrl) {
      setLoading(false)
    }
  }, [hasRedirectUrl, loading])

  useEffect(() => {
    if (hasRedirectUrl) {
      localStorage.removeItem("redirectUrl")
    }

    if (authenticated && hasRedirectUrl) {
      push(redirectUrl)
      return
    }
  }, [authenticated, hasRedirectUrl, loading, mode, push, redirectUrl])

  const {
    status: userLoginStatus,
    userData,
    updateUserPreferredPronoun,
    updateUserLanguage,
  } = useUser()
  const { language: userLanguage } = useContext(LanguageContext)
  const [selectedLanguage, setSelectedLanguage] = useState(userLanguage)

  const handleAreaChange = (e) => {
    setArea(e.target.value)
  }

  const handleTypeChange = (e) => {
    setType(e.target.value)
  }

  const handleDurationChange = (e) => {
    setDuration(e.target.value)
  }

  const handleLanguageChange = (value) => {
    setSelectedLanguage(value)
    setPronoun("")
  }

  const getLanguagePronounsOptions = useCallback(
    (language) => {
      const defaultPronouns = languagesPronouns[language.substring(0, 2)].map(
        (name) => ({
          value: name,
          label: name,
        })
      )

      defaultPronouns.push({
        value: "other-specify",
        label: t("HOME_AREA_OTHER_SPECIFY"),
      })

      return defaultPronouns
    },
    [t]
  )

  const handleNameChange = (e) => {
    setName(e.target.value)
  }

  const handleApproachChange = (e) => {
    setDuration("")
    setApproach(e.target.value)
  }

  const handlePronounChange = (e) => {
    setPronoun(e.target.value)
  }

  const handleAreaCustomChange = (e) => {
    setAreaCustom(e.target.value)
  }

  const handlePronounCustomChange = (e) => {
    setPronounCustom(e.target.value)
  }

  const saveUserPreferences = useCallback(async () => {
    try {
      await updateUserPreferredPronoun(pronoun)
      await updateUserLanguage(selectedLanguage)
    } catch (e) {
      console.error("Error on saving users preferences", e)
    }
  }, [
    pronoun,
    selectedLanguage,
    updateUserPreferredPronoun,
    updateUserLanguage,
  ])

  const startSession = useCallback(async () => {
    setLoadingStartSession(true)
    // await saveUserPreferences()

    const gender = () => {
      if (solaceGenders.male.includes(pronoun)) {
        return "male"
      }

      if (solaceGenders.female.includes(pronoun)) {
        return "female"
      }

      return "trans"
    }

    const pronounSelected = pronounCustom !== "" ? pronounCustom : pronoun

    const sessionUrl = buildAbsoluteUrl(
      `${selectedLanguage}/session${
        [
          "personal-development",
          "career-progression",
          "interview-training",
          "scholl-issues",
          "sports",
        ].includes(area)
          ? "-coach"
          : ""
      }/?name=${name}&gender=${gender()}&pronoun=${pronounSelected}&area=${area}&approach=${approach}&other-specify=${areaCustom}&type=${type}&duration=${duration}`
    )

    const goToSession = () => {
      window.location.href = sessionUrl
    }

    // if (!authenticated) {
    //   localStorage.setItem("redirectUrl", sessionUrl)
    //   goToSignUp()
    //   return
    // }

    if (selectedLanguage && name && pronoun) {
      goToSession()
    }
  }, [
    selectedLanguage,
    name,
    pronoun,
    pronounCustom,
    area,
    approach,
    areaCustom,
    type,
    duration,
  ])

  const isMainButtonDisabled = useMemo(() => {
    if (loading || loadingStartSession) {
      return true
    }

    if (step === 0) {
      return !type
    }

    if (step === 1) {
      return (
        !area ||
        (["other-specify", "interview-training"].includes(area) && !areaCustom)
      )
    }

    if (step === 2) {
      return !approach
    }
    if (step === 3) {
      return !duration
    }

    if (step === 4) {
      return !selectedLanguage
    }

    if (step === 5) {
      return !name
    }

    return !pronoun
  }, [
    loading,
    loadingStartSession,
    step,
    selectedLanguage,
    name,
    pronoun,
    type,
    area,
    approach,
    areaCustom,
    duration,
  ])

  const scrollUp = () => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      })
    }, 50)
  }

  const handleBack = () => {
    setStep(step - 1)
    scrollUp()
  }

  const handleMainButton = useCallback(async () => {
    if (isMainButtonDisabled) {
      return
    }
    scrollUp()

    if (step === 6) {
      startSession()
      return
    }

    setStep(step + 1)
  }, [step, startSession, isMainButtonDisabled])

  const stepFields = useMemo(() => {
    const sessionType = (
      <FormInput
        label={t("HOME_SESSION_TYPE_LABEL")}
        description={t("HOME_SESSION_TYPE_SUB_LABEL")}
        type="select-boxes"
        options={typeOptions(t)}
        value={type}
        onChange={handleTypeChange}
        disabled={loading}
      />
    )

    const sessionTypeWithRandomFact = (
      <>
        {sessionType}
        <RandomFact num={3} />
      </>
    )

    return {
      0: mode === "voxme" ? sessionType : sessionTypeWithRandomFact,
      1: (
        <>
          <FormInput
            label={t("HOME_AREA_LABEL")}
            type="select-boxes"
            options={areaOptions(mode, t)}
            value={area}
            onChange={handleAreaChange}
            disabled={loading}
          />
          {area === "other-specify" && (
            <FormInput
              type="text"
              value={areaCustom}
              onChange={handleAreaCustomChange}
              disabled={loading}
            />
          )}
          {area === "interview-training" && (
            <FormInput
              label={t("HOME_AREA_INTERVIEW_TRAINING_LABEL")}
              type="textarea"
              textareaMaxLength={1500}
              textareaRows={12}
              value={areaCustom}
              onChange={handleAreaCustomChange}
              disabled={loading}
            />
          )}
        </>
      ),
      2: (
        <>
          <FormInput
            label={t("HOME_SESSION_APPROACH_LABEL")}
            type="select-boxes"
            options={approachOptions(t)}
            value={approach}
            onChange={handleApproachChange}
            disabled={loading}
          />
          <RandomFact num={1} />
        </>
      ),
      3: (
        <FormInput
          label={t("HOME_SESSION_DURATION_LABEL")}
          type="select-boxes"
          options={durationOptions(approach, t)}
          value={duration}
          onChange={handleDurationChange}
          disabled={loading}
        />
      ),
      4: (
        <FormInput
          label={t("HOME_NAME_LABEL")}
          type="text"
          value={name}
          onChange={handleNameChange}
          disabled={loading}
        />
      ),
      5: (
        <>
          <CountryAndLanguageSelector
            label={t("HOME_LANGUAGE_LABEL")}
            className="flex flex-col justify-left items-left flex-2 lg:px-2"
            onSelect={handleLanguageChange}
            language={selectedLanguage}
            showSelectedLabel={true}
            transparent={true}
          />

          <RandomFact num={2} />
        </>
      ),
      6: (
        <>
          <FormInput
            label={t("HOME_PRONOUN_LABEL")}
            type="select-boxes"
            options={getLanguagePronounsOptions(selectedLanguage)}
            value={pronoun}
            onChange={handlePronounChange}
            disabled={loading}
          />

          {pronoun === "other-specify" && (
            <FormInput
              type="text"
              value={pronounCustom}
              onChange={handlePronounCustomChange}
              disabled={loading}
            />
          )}
        </>
      ),
    }
  }, [
    approach,
    area,
    areaCustom,
    duration,
    getLanguagePronounsOptions,
    loading,
    mode,
    name,
    pronoun,
    pronounCustom,
    selectedLanguage,
    t,
    type,
  ])

  useEffect(() => {
    if (loading && userLoginStatus === "logged-in") {
      setName(userData.name)
      if (userData.language) {
        setSelectedLanguage(userData.language)
      }

      if (userData.preferredPronoun) {
        setPronoun(userData.preferredPronoun)
      }
    }
  }, [
    loading,
    userLoginStatus,
    userData,
    setName,
    setSelectedLanguage,
    setPronoun,
  ])

  const mainButtonLabel = useMemo(() => {
    const labelNext = (
      <div className="flex items-center">
        {t("HOME_NEXT")}
        <Image
          width={24}
          height={24}
          className="ml-2"
          src="/assets/icon-arrow-right-white.svg"
          alt="arrow-right"
        />
      </div>
    )

    const stepLabels = [
      labelNext,
      labelNext,
      labelNext,
      labelNext,
      labelNext,
      labelNext,
      t("HOME_START_SESSION"),
    ]

    if (step === 6 && loadingStartSession) {
      return t("COMMON_LOADING")
    }
    return stepLabels[step]
  }, [t, step, loadingStartSession])

  return loading ? (
    <SessionStepsLoading />
  ) : (
    <>
      <ProgressSteps step={step < 4 ? 0 : step} stepNames={stepNames(t)} />
      {stepFields[step]}
      <div className="flex justify-between w-full h-20 relative">
        {step !== 0 && (
          <Button onClick={handleBack} style="secondary">
            <div className="flex items-center">
              <Image
                width={24}
                height={24}
                className="mr-2"
                src="/assets/icon-arrow-left-black.svg"
                alt="arrow-right"
              />

              {t("HOME_BACK")}
            </div>
          </Button>
        )}

        <Button
          onClick={handleMainButton}
          style="primary"
          className={` right-0 absolute text-lg sm:text-2xl ${
            isMainButtonDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={isMainButtonDisabled}
        >
          {mainButtonLabel}
        </Button>
      </div>
    </>
  )
}

export default SessionSteps
